import {
  Button,
  Card,
  CardPreview,
  makeStyles,
  Text,
  tokens,
} from "@fluentui/react-components";
import React from "react";
import { Sign } from "../sign-generator/Sign";
import { ArrowRightFilled } from "@fluentui/react-icons";
import { useTranslation } from "react-i18next";
import { ISignCard } from "../../interfaces/ISignCard";
import { ISignGenerate } from "../../interfaces/ISignGenerate";
import {
  calculateScaledDimensions,
  generateSignFromTemplate,
} from "../../helper/signGeneratorHelper";

type SignCardProps = {
  sign: ISignCard;
  setGenerateSign: React.Dispatch<React.SetStateAction<ISignGenerate>>;
};

const useStyles = makeStyles({
  card: {
    maxWidth: "100%",
    marginRight: "64px",
    minWidth: "300px",
    height: "100%",
  },
  contentWrapper: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    "> div": {
      marginTop: tokens.spacingVerticalXXXL,
      marginLeft: tokens.spacingHorizontalXXL,
      marginRight: tokens.spacingHorizontalXXL,
    },
    "> h2": {
      marginTop: tokens.spacingVerticalXXL,
      marginBottom: tokens.spacingVerticalL,
      marginLeft: tokens.spacingHorizontalXXL,
      fontSize: tokens.fontSizeBase400,
      fontWeight: tokens.fontWeightBold,
    },
    "> p": {
      marginLeft: tokens.spacingHorizontalXXL,
      fontSize: tokens.fontSizeBase400,
      marginBottom: tokens.spacingVerticalS,
    },
    "@media (max-width: 1700px)": {
      "> div": {
        marginTop: tokens.spacingVerticalL,
      },
      "> h2": {
        marginTop: tokens.spacingVerticalM,
      },
      "> p": {
        marginTop: tokens.spacingVerticalS,
      },
    },
  },
  imageContainer: {
    display: "flex !important",
    flexDirection: "column",
    justifyContent: "flex-end",
    minHeight: "380px",
    "@media (max-width: 1700px)": {
      minHeight: "230px",
    },
  },
  footer: {
    display: "flex !important",
    justifyContent: "space-between",
    alignItems: "center",
    marginLeft: tokens.spacingHorizontalXXL,
    marginRight: tokens.spacingHorizontalXXL,
    marginBottom: tokens.spacingVerticalXXXL,
    "> p": {
      fontSize: tokens.fontSizeBase400,
    },
    "> button": {
      marginRight: "48px",
    },
  },
  button: {
    display: "flex",
    alignItems: "center",
    "> svg": {
      marginLeft: tokens.spacingHorizontalS,
    },
  },
});

export const SignCard = ({ sign, setGenerateSign }: SignCardProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const maxDimension = window.innerWidth < 1700 ? 150 : 250;

  const { width } = calculateScaledDimensions(
    sign.sign.width,
    sign.sign.height,
    maxDimension
  );
  return (
    <Card className={styles.card}>
      <CardPreview className={styles.contentWrapper}>
        <div className={styles.imageContainer}>
          <div>
            <img
              src={sign?.signSymbol}
              style={{ width: `${width}px` }}
              alt={sign?.signSymbolAlt}
            />
          </div>
          <Sign sign={sign.sign} isTemplate landingPage />
        </div>
        <Text as="h2">{sign.header}</Text>
        <Text as="p">{sign.subText}</Text>
        <div className={styles.footer}>
          <Text as="p">{sign.footerText}</Text>
          <Button
            appearance="primary"
            size="large"
            onClick={() =>
              setGenerateSign({
                generate: true,
                defaultValues: generateSignFromTemplate(sign.sign),
              })
            }
          >
            <div className={styles.button}>
              <Text>{t("button.newDesign")}</Text> <ArrowRightFilled />
            </div>
          </Button>
        </div>
      </CardPreview>
    </Card>
  );
};
